import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable, of, switchMap, throwError } from 'rxjs';
import { AuthUtils } from './auth.utils';
import { UserService } from '../user/user.service';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from 'src/app/services/local-storage';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _authenticated: boolean = false;
  private _httpClient = inject(HttpClient);
  private storage= inject(LocalStorageService);
  private _userService= inject(UserService);

  /**
   * Constructor
   */

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Setter & getter for access token
   */
  set accessToken(token: string) {
    localStorage.setItem('accessToken', token);
  }

  get accessToken(): string {
    return localStorage.getItem('accessToken') ?? '';
  }

  /**
   * Setter & getter for access token
   */
  set expiresIn(expires_in: string) {
    localStorage.setItem('expires_in', expires_in);
  }

  get expiresIn(): string {
    return localStorage.getItem('expires_in') ?? '';
  }
  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Forgot password
   *
   * @param email
   */
  forgotPassword(email: string): Observable<any> {
    // return this._httpClient.post('api/auth/forgot-password', email);

    // const deviceInfo = await this.apiService.getDeviceInfo();

    const tokenEndpoint: string = environment.baseUrl + 'api/account/recoverpassword';

    const params = {
      Email: email,
      // deviceInfo,
    };

    // return this.apiService.post(tokenEndpoint, params);
    return this._httpClient.post(tokenEndpoint, params);
  }

  /**
   * Reset password
   *
   * @param password
   */
  resetPassword(password: string): Observable<any> {
    let params = {
      OldPassword: password,
      NewPassword: password,
      ConfirmPassword: password,
    };
    const tokenEndpoint: string = environment.baseUrl + 'api/account/changepassword';

    return this._httpClient.post(tokenEndpoint, params);
  }

  agree_terms(email: string) {
    const tokenEndpoint: string = environment.api('api/Account/accept_terms');

    const params: any = {
      email,
    };

    return this._httpClient.post(tokenEndpoint, params);
  }

  /**
   * Sign in
   *
   * @param credentials
   */
  signIn(credentials: { username: string; password: string; grant_type: 'password' }): Observable<any> {
    // Throw error, if the user is already logged in
    if (this._authenticated) {
      return throwError('User is already logged in.');
    }

    const tokenEndpoint: string = environment.api('Token');
    const body: string = this.encodeParams(credentials);

    return this._httpClient.post(tokenEndpoint, body).pipe(
      switchMap((response: any) => {
        console.log("🚀 ~ AuthService ~ switchMap ~ response:", JSON.stringify(response))
        // Store the access token in the local storage
        this.accessToken = response.access_token;
        this.expiresIn = response.expires_in;
        this.storage.setStorage('IdUser', response.Id);

        // Set the authenticated flag to true
        this._authenticated = true;

        // Store the user on the user service
        this._userService.user = {
          id: response.id,
          email: response.email,
          name: response.name,
          avatar: '',
          image: response.avatar,
          accountreal: response.accountreal,
          accountdemo: response.accountdemo,
          generationid: response.generationid,
        };

        // Return a new observable with the response
        return of(response);
      }),
    );
  }

  /**
   * Sign in using the access token
   */
  signInUsingToken(): Observable<any> {
    // Renew token
    return this._httpClient
      .post('api/auth/refresh-access-token', {
        accessToken: this.accessToken,
      })
      .pipe(
        catchError(() =>
          // Return false
          of(false),
        ),
        switchMap((response: any) => {
          // Store the access token in the local storage
          this.accessToken = response.accessToken;

          // Set the authenticated flag to true
          this._authenticated = true;

          // Store the user on the user service
          this._userService.user = response.user;

          // Return true
          return of(true);
        }),
      );
  }

  /**
   * Sign out
   */
  signOut(): Observable<any> {
    // Remove the access token from the local storage
    localStorage.removeItem('accessToken');
    localStorage.removeItem('expires_in');

    // Set the authenticated flag to false
    this._authenticated = false;

    // Return the observable
    return of(true);
  }

  /**
   * Sign up
   *
   * @param user
   */
  signUp(user: {
    name: string;
    email: string;
    password: string;
    // company: string;
  }): Observable<any> {
    const tokenEndpoint: string = environment.api('api/account/register');

    const params: any = {
      Email: user.email,
      Name: user.name,
      Password: user.password,
    };

    return this._httpClient.post(tokenEndpoint, params);
  }

  /**
   * Unlock session
   *
   * @param credentials
   */
  unlockSession(credentials: { email: string; password: string }): Observable<any> {
    return this._httpClient.post('api/auth/unlock-session', credentials);
  }

  /**
   * Check the authentication status
   */
  check(): Observable<boolean> {
    // Check if the user is logged in
    if (this._authenticated) {
      return of(true);
    }

    // Check the access token availability
    if (!this.accessToken) {
      return of(false);
    }

    // Check the access token expire date
    if (AuthUtils.isTokenExpired(this.expiresIn)) {
      return of(false);
    }

    // If the access token exists and it didn't expire, sign in using it
    // return this.signInUsingToken();
    return of(true);
  }

  public encodeParams(params: any): string {
    let body = '';
    for (const key in params) {
      if (body.length) {
        body += '&';
      }
      body += key + '=';
      body += encodeURIComponent(params[key]);
    }

    return body;
  }
}
